<template>
  <div class="flex overflow-hidden z-10 flex-col h-full max-h-screen bg-white transition-all duration-300 ease-out transform lg:ease-in lg:pb-0 lg:duration-150" :class="[ showNoteListMobile ? 'opacity-100 scale-100' : 'opacity-0 scale-90 lg:scale-100 lg:opacity-100' ]">
    <div class="flex items-center p-2 h-12 lg:h-auto">
      <div class="block lg:hidden">
        <a @click.prevent="$store.commit('toggleSidebar')" class="btn-icon-circle" title="Menu">
          <img src="@/assets/icons/icon-nav-menu.svg"/>
        </a>
      </div>
      <div class="overflow-hidden text-xl font-semibold leading-loose whitespace-nowrap transition-all ease-out tracking-xl overflow-ellipsis" :class="[ searchOpen ? 'transform -translate-x-20 w-0 mx-0 p-0' : 'mr-2 ml-2 lg:ml-7 w-full' ]">
        {{ pageTitle }}
      </div>
      <div class="relative flex-grow mx-2 text-right group">
        <input @input="onInputQuery" class="h-10 text-sm placeholder-transparent text-transparent rounded-full transition-all duration-200 ease-out group-focus:border-gray-500 focus:placeholder-gray-400 focus:text-black" :class="[ searchOpen ? 'py-2.5 pr-8 pl-5 w-full border bg-white border-gray-400 placeholder-gray-400' : 'py-2 px-4 w-10 bg-transparent group-hover:bg-gray-100' ]" ref="search" placeholder="Search Note" @keyup.esc="onCloseSearch"/>
        <span v-if="searchOpen" class="absolute top-0 right-0 z-10 p-2 leading-3 opacity-50 cursor-pointer group-hover:opacity-75" title="Press ESC to close" @click.prevent="onCloseSearch">
          <img src="../assets/icons/icon-close.svg"/>
        </span>
        <span v-else class="absolute top-0 right-0 z-10 p-2 leading-3 opacity-50 cursor-pointer" title="Search Note" @click="openSearchBar">
          <img src="../assets/icons/icon-search.svg"/>
        </span>
      </div>
      <v-popover v-if="this.$route.params.folderId !== 'archived'" placement="bottom">
        <a title="Create New Note" class="btn-icon-circle">
          <img src="../assets/icons/note/icon-new-note.svg"/>
        </a>
        <template slot="popover">
          <div class="pt-2 pop-menu">
            <div class="pb-3 pop-menu-title">Templates</div>
            <div class="pop-menu-item">
              <a v-close-popover @click.prevent="onCreateNoteClick">
                <img src="../assets/icons/note/icon-new-note.svg"/> Blank Note
              </a>
            </div>
            <div class="pop-menu-item">
              <a v-close-popover @click.prevent="onCreateNoteTemplate('project')">
                <img src="../assets/icons/note/icon-note-template.svg"/> Project Plan
              </a>
            </div>
            <div class="pop-menu-item">
              <a v-close-popover @click.prevent="onCreateNoteTemplate('journal')">
                <img src="../assets/icons/note/icon-note-template.svg"/> Daily Journal
              </a>
            </div>
            <div class="pop-menu-item">
              <a v-close-popover @click.prevent="onCreateNoteTemplate('meeting')">
                <img src="../assets/icons/note/icon-note-template.svg"/> Meeting Note
              </a>
            </div>
          </div>
        </template>
      </v-popover>
      <a v-else
        title="Delete All Archived"
        class="btn-icon-circle"
        @click.prevent="$emit('delete-all-archived')"
      >
        <img src="../assets/icons/icon-delete.svg"/>
      </a>
    </div>
    <div class="overflow-y-auto overflow-x-hidden h-full" v-if="notes.length > 0">
      <div v-if="loading || !selectedFolder" class="overflow-hidden relative h-full">
        <Loading/>
      </div>
      <div v-else class="overflow-hidden w-full lg:w-90" :class="{ 'opacity-30': isSearching }">
        <h6 class="mt-3 mb-3 ml-9 text-xs font-semibold leading-4 text-gray-500 uppercase tracking-px" v-if="pinnedNotes.length > 0">Pinned</h6>
        <note-item
          v-for="note in pinnedNotes" :key="note._id"
          :note="note" :query="query" :snippet="snippets[note._id]" :selectedNoteId="selectedNoteId"
          :onSelectNote="onSelectNote"
        />
        <h6 class="mt-9 mb-3 ml-9 text-xs font-semibold leading-4 text-gray-500 uppercase tracking-px" v-if="pinnedNotes.length > 0">Others</h6>
        <note-item
          v-for="note in unpinnedNotes" :key="note._id"
          :note="note" :query="query" :snippet="snippets[note._id]" :selectedNoteId="selectedNoteId"
          :onSelectNote="onSelectNote"
        />
      </div>
    </div>
    <div v-else-if="query.length > 0">
      <div class="py-4 px-9 text-gray-400">
        No note found
      </div>
    </div>
    <div v-else>
      <div v-if="this.$route.params.folderId !== 'archived'" class="py-12 px-6 h-full text-center text-gray-400">
        <a class="pb-0.5 border-b border-gray-300" @click.prevent="onCreateNoteClick">Create a new note</a>
      </div>
      <div v-else class="py-4 px-9 text-gray-400">
        No archived note
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es'
import feathersClient from '@/feathers-client'
import Loading from '@/components/Loading'
import NoteItem from '@/components/NoteItem'
import { NOTE_LIST_FIELDS } from '@/lib/notes'
import { matches } from '@/lib/search'

export default {
  props: [
    'loading',
    'filteredNotes',
    'selectedNoteId',
    'selectedFolder',
    'showNoteListDesktop',
    'showNoteListMobile'
  ],
  components: { Loading, NoteItem },
  data() {
    return {
      query: '',
      searchOpen: false,
      isSearching: false,
      // id -> a window of the matching text, for notes the server matched on
      // their full body. Both what makes them appear in the list at all and
      // what the preview shows, since the excerpt does not reach that far.
      snippets: {}
    }
  },
  computed: {
    pageTitle() {
      return this.$route.params.folderId === 'archived'
        ? 'Archived'
        : this.selectedFolder?.title ?? 'All Notes'
    },
    // Search used to scan every note body in the browser, which only worked
    // because every body had been downloaded. Titles and excerpts are matched
    // locally so results appear as you type; the server matches the full bodies
    // in parallel and its hits merge in when they arrive.
    notes() {
      if (!this.query) return this.filteredNotes
      return this.filteredNotes.filter(note => {
        if (matches(note.title, this.query)) return true
        if (matches(note.excerpt, this.query)) return true
        // an open note matches on its unsaved edits too
        if (typeof note.content === 'string' && matches(note.content, this.query)) return true
        return this.snippets[note._id] !== undefined
      })
    },
    pinnedNotes() {
      return this.notes.filter(note => note.pinned)
    },
    unpinnedNotes() {
      return this.notes.filter(note => !note.pinned)
    }
  },
  created() {
    this.updateQuery = debounce(this.updateQuery, 750)
  },
  methods: {
    onInputQuery(event) {
      this.isSearching = true
      this.updateQuery(event)
    },
    updateQuery(event) {
      this.query = event.target.value
      this.isSearching = false
      this.searchBodies(this.query)
    },
    // Goes to the service rather than through the store: the response carries a
    // snippet per note, which is per-search and not part of a note, and a store
    // dispatch would hand back Note instances with it dropped.
    //
    // The raw text is sent as-is. The server decides what pattern runs -- it
    // splits the terms, escapes them and bounds the gaps -- so escaping here
    // would only escape the escapes.
    searchBodies(query) {
      if (!query) {
        this.snippets = {}
        return
      }
      feathersClient.service('notes').find({
        query: {
          content: { $regex: query, $options: 'i' },
          $select: NOTE_LIST_FIELDS
        }
      })
        .then(result => {
          // a slower earlier search must not overwrite a newer one
          if (query !== this.query) return
          const list = Array.isArray(result) ? result : (result.data || [])
          this.snippets = list.reduce((acc, note) => {
            acc[note._id] = note.snippet || ''
            return acc
          }, {})
        })
        .catch(() => { this.snippets = {} })
    },
    openSearchBar() {
      if (this.searchOpen) {
        this.searchOpen = false
      } else {
        this.searchOpen = true
        this.$nextTick(() => this.$refs.search.focus())
      }
    },
    onCreateNoteClick() {
      this.$emit('create-note')
    },
    onCreateNoteTemplate(type) {
      this.$emit('create-note-template', type)
    },
    onSelectNote(note) {
      this.$emit('select-note', note._id)
    },
    onCloseSearch(e) {
      this.$refs.search.value = ''
      this.query = ''
      this.searchOpen = false
      e.target.blur()
    }
  }
}
</script>
